// =========================================================================
// HelpyDoc · Overlay REMOTO de platos recomendados (merge por id)
// =========================================================================
// Permite editar/añadir platos desde una fuente en Drive (→ content/platos.json)
// SIN redeploy, sobre el catálogo base (public/data/actividad.json, 81 platos
// curados en prod). Estrategia MERGE POR ID (decisión de Carlos): el overlay solo
// sobrescribe/añade por id; los platos no mencionados se conservan. Un error en el
// remoto NO borra el catálogo (fallback embebido vía remote-content).
// =========================================================================

import type { Plato } from '@/services/DataService';
import { loadRemoteContent, isObject } from '@/lib/remote-content';

/** Entrada del overlay: id obligatorio + cualquier campo de Plato a sobrescribir. */
export type PlatoOverlay = Partial<Plato> & { id: string };

export interface PlatosManifest {
  version: number;
  activo?: boolean;
  platos: PlatoOverlay[];
}

function isPlatosManifest(raw: unknown): raw is PlatosManifest {
  if (!isObject(raw)) return false;
  if (typeof raw.version !== 'number') return false;
  if (!Array.isArray(raw.platos)) return false;
  return raw.platos.every((p) => isObject(p) && typeof p.id === 'string');
}

/** Campos numéricos con default 0 al crear un plato nuevo desde el overlay. */
function completeNuevoPlato(o: PlatoOverlay): Plato | null {
  // Un plato NUEVO (id inexistente en base) necesita al menos nombre.
  if (!o.nombre || typeof o.nombre !== 'string') return null;
  return {
    id: o.id,
    nombre: o.nombre,
    descripcion: o.descripcion ?? '',
    categoria: o.categoria ?? 'general',
    calorias: o.calorias ?? 0,
    proteinas: o.proteinas ?? 0,
    carbohidratos: o.carbohidratos ?? 0,
    grasas: o.grasas ?? 0,
    tiempo: o.tiempo ?? 0,
    dificultad: o.dificultad ?? 'media',
    etiquetas: o.etiquetas ?? [],
    ingredientes: o.ingredientes ?? [],
    imagen: o.imagen,
  };
}

/**
 * Fusiona el overlay sobre el catálogo base por id:
 *  • id existente → sobrescribe los campos presentes (el resto se conserva).
 *  • id nuevo → añade el plato (si trae al menos nombre).
 * Nunca elimina platos base. Puro/determinista.
 */
export function mergePlatos(base: Plato[], overlay: PlatoOverlay[]): Plato[] {
  const byId = new Map(base.map((p) => [p.id, { ...p }]));
  for (const o of overlay) {
    const prev = byId.get(o.id);
    if (prev) {
      byId.set(o.id, { ...prev, ...o });
    } else {
      const nuevo = completeNuevoPlato(o);
      if (nuevo) byId.set(o.id, nuevo);
    }
  }
  return [...byId.values()];
}

/**
 * Aplica el overlay remoto (Drive → JSON) al catálogo base. Si no hay overlay
 * o falla, devuelve el catálogo base sin cambios (no-breaking).
 */
export async function applyPlatosOverlay(base: Plato[]): Promise<Plato[]> {
  const manifest = await loadRemoteContent<PlatosManifest | null>(
    'platos', null, isPlatosManifest,
  );
  if (!manifest || manifest.activo === false) return base;
  return mergePlatos(base, manifest.platos);
}
